import axios from 'axios';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { gravarNaSessao, lerDaSessao } from '../hooks/useFiltroDaSessao';
import api from '../service/api';
import { useAuth } from './AuthContext';

// A conversa com o Assistente ESF vive aqui, e não no balão, para que o balão
// flutuante e a página /assistente mostrem a MESMA conversa: começa num, segue
// no outro. Também por isso a resposta continua sendo escrita ao trocar de tela.

// Endereço do assistente (o MCP da Suplen, pasta mcp-suplen).
// Em `npm start`, fala com o MCP rodando na máquina
// (`python -m suplen_mcp.web`, porta 8020); no build, com o publicado no
// Easypanel. Trocou o endereço? Troque aqui E no connect-src do CSP
// (etc/nginx.conf e web.config): sem isso o navegador bloqueia a chamada.
// REACT_APP_ASSISTENTE_URL, no build, manda mais que os dois.
const ASSISTENTE_URL =
  process.env.REACT_APP_ASSISTENTE_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://127.0.0.1:8020/api/assistente/chat'
    : 'https://mcp-mcp-suplen.o1fvnz.easypanel.host/api/assistente/chat');

// O painel de inteligência comercial mora no mesmo MCP, ao lado do chat. Sem o
// MCP direto (o caminho antigo, pelo ESF-API), não há painel.
export const PAINEL_URL = ASSISTENTE_URL
  ? ASSISTENTE_URL.replace(/\/chat$/, '/painel')
  : '';

// A gaveta do painel investiga a variação da venda de uma análise, na rota ao
// lado do painel, no mesmo MCP (e no mesmo connect-src do CSP).
export const INVESTIGACAO_URL = PAINEL_URL
  ? PAINEL_URL.replace(/\/painel$/, '/investigacao')
  : '';

// Os gráficos que esta tela desenha (GraficoInterativo.tsx). O MCP só manda estes: um
// tipo novo lá não quebra esta versão do web.
export const TIPOS_DE_GRAFICO = ['barras', 'linha', 'pizza', 'mapa_de_calor'];

// Como cada tela pede a resposta ao MCP: as duas desenham Markdown; o balão,
// estreito, pede uma resposta curta e sem tabela.
export type FormatoResposta = 'balao_markdown' | 'pagina';

// Um gráfico da resposta, como o MCP manda (suplen_mcp/grafico.py). A tela
// desenha abaixo do texto; os números vêm das consultas, não da LLM. No mapa
// de calor, cada série é uma linha (o dia) e cada rótulo, uma coluna (a hora).
export interface Grafico {
  tipo: 'barras' | 'linha' | 'pizza' | 'mapa_de_calor';
  titulo: string;
  unidade: 'reais' | 'percentual' | 'quantidade';
  rotulos: string[];
  series: { nome: string; valores: number[] }[];
}

export interface Mensagem {
  autor: 'ia' | 'usuario';
  texto: string;
  graficos?: Grafico[];
}

// Uma filial do ESF ("empresa" no banco). A Suplen tem oito (0101 a 0105 da
// Suplen Medical, 0301 a 0303 da NeuroVasc), e a `user.empresa.id` do login
// não é a filial em que os pedidos estão: o app grava o pedido na filial
// ATIVA do vendedor (ver ListOrder). Por isso quem usa o assistente escolhe.
export interface Filial {
  id: string;
  codigo: string;
  nome: string;
}

// Mesmas regras do MCP (`listar_empresas`): fora a excluída e a inativa.
export function filiaisAtivas(empresas: any): Filial[] {
  return (Array.isArray(empresas) ? empresas : [])
    .filter(
      (e: any) =>
        e?.empresa_id &&
        !e?.excluido &&
        ['', 'ATIVO'].includes(String(e?.status_empresa || '').trim().toUpperCase())
    )
    .map((e: any) => ({
      id: String(e.empresa_id),
      codigo: String(e.filial || '').trim(),
      nome: String(e.fantasia || e.razao_social || '').trim(),
    }))
    .sort((a, b) => (a.codigo || a.nome).localeCompare(b.codigo || b.nome));
}

export function rotuloDaFilial(filial: Filial): string {
  return filial.codigo ? `${filial.codigo} · ${filial.nome}` : filial.nome;
}

// Uma empresa: as filiais com os mesmos 2 primeiros dígitos (01xx Suplen
// Medical, 03xx NeuroVasc), com o nome que todas têm no começo. É a mesma regra
// do MCP (`esf_client.grupos_de_filiais`), que soma as filiais no painel da
// empresa. Filial sem código de 4 dígitos não entra em empresa nenhuma.
export interface Empresa {
  codigo: string;
  nome: string;
  filiais: Filial[];
}

function nomeEmComum(nomes: string[]): string {
  const palavras = nomes.filter(Boolean).map((n) => n.split(/\s+/));
  if (!palavras.length) return '';
  const comum: string[] = [];
  for (let i = 0; i < palavras[0].length; i += 1) {
    const palavra = palavras[0][i];
    if (!palavras.every((p) => p[i] === palavra)) break;
    comum.push(palavra);
  }
  return comum.join(' ') || palavras[0].join(' ');
}

export function empresasDasFiliais(filiais: Filial[]): Empresa[] {
  const grupos = new Map<string, Filial[]>();
  filiais.forEach((f) => {
    if (!/^\d{4}$/.test(f.codigo)) return;
    const codigo = f.codigo.slice(0, 2);
    grupos.set(codigo, [...(grupos.get(codigo) || []), f]);
  });
  return Array.from(grupos.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([codigo, doGrupo]) => ({
      codigo,
      nome: nomeEmComum(doGrupo.map((f) => f.nome)),
      filiais: doGrupo,
    }));
}

export function rotuloDaEmpresa(empresa: Empresa): string {
  return `${empresa.codigo} · ${empresa.nome}`;
}

// Sugestões de "acesso rápido" exibidas quando a conversa está vazia.
export const SUGESTOES = [
  'Como estão as vendas este mês?',
  'Resumo dos pedidos a faturar',
  'Quais pedidos estão aguardando aprovação?',
];

// A resposta aparece como se alguém digitasse: palavra por palavra, cada uma
// no tempo de digitá-la, com um ritmo um pouco irregular e pausas depois de
// vírgula, de ponto e na quebra de linha. Resposta longa acelera por igual
// para terminar em até 4 s, para o usuário não achar que travou.
const MS_POR_LETRA = 28;
const VARIACAO_MS = 40;
const PAUSA_VIRGULA_MS = 90;
const PAUSA_FRASE_MS = 220;
const PAUSA_LINHA_MS = 300;
const DURACAO_MAX_ESCRITA_MS = 4000;

// Divide a resposta em palavras (cada uma com o espaço que vem depois) e diz,
// para cada uma, até onde mostrar o texto e quanto esperar antes da próxima.
// Cortar só entre palavras também evita partir um emoji ao meio.
function planoDeEscrita(texto: string): { ate: number; espera: number }[] {
  const palavras = texto.match(/\S+\s*|\s+/g) ?? [texto];
  let ate = 0;
  const plano = palavras.map((palavra) => {
    ate += palavra.length;
    const semEspaco = palavra.trimEnd();
    let espera = semEspaco.length * MS_POR_LETRA + Math.random() * VARIACAO_MS;
    if (/[.!?…]$/.test(semEspaco)) espera += PAUSA_FRASE_MS;
    else if (/[,;:]$/.test(semEspaco)) espera += PAUSA_VIRGULA_MS;
    if (palavra.includes('\n')) espera += PAUSA_LINHA_MS;
    return { ate, espera };
  });
  const total = plano.reduce((soma, passo) => soma + passo.espera, 0);
  const fator = total > DURACAO_MAX_ESCRITA_MS ? DURACAO_MAX_ESCRITA_MS / total : 1;
  return plano.map((passo) => ({ ate: passo.ate, espera: passo.espera * fator }));
}

interface AssistenteContextData {
  mensagens: Mensagem[];
  // A pergunta foi enviada e a resposta ainda não chegou.
  carregando: boolean;
  // A resposta já chegou e está aparecendo aos poucos.
  escrevendo: boolean;
  enviar: (pergunta: string, formato: FormatoResposta) => void;
  novaConversa: () => void;
  // As filiais que dá para escolher (vazio enquanto carrega ou se falhou).
  filiais: Filial[];
  carregandoFiliais: boolean;
  erroFiliais: boolean;
  recarregarFiliais: () => void;
  // As empresas (as filiais agrupadas pelos 2 primeiros dígitos do código).
  empresas: Empresa[];
  // A empresa escolhida ("01"), ou vazio: sem ela o painel não carrega.
  empresaCodigo: string;
  escolherEmpresa: (codigo: string) => void;
  // A filial da empresa (`empresa_id`), ou vazio = todas as filiais dela.
  filialId: string;
  escolherFilial: (id: string) => void;
  // A região do cliente ("009", ver helpers/regioes.ts), ou vazio = todas.
  regiao: string;
  escolherRegiao: (codigo: string) => void;
}

const AssistenteContext = createContext<AssistenteContextData>(
  {} as AssistenteContextData
);

interface AssistenteProviderProps {
  children: ReactNode;
}

export function AssistenteProvider({ children }: AssistenteProviderProps) {
  const { user } = useAuth();
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [escrevendo, setEscrevendo] = useState(false);
  const timerEscrita = useRef<number>();
  // Cada conversa tem um número: a resposta de uma conversa já apagada (nova
  // conversa, troca de usuário) é descartada quando chega.
  const conversa = useRef(0);

  // A empresa escolhida (e, se quiser, a filial dela) vale para o painel e para
  // o chat, e fica lembrada na aba (sessionStorage, como os filtros das telas),
  // por usuário: há computador compartilhado.
  const chaveEmpresa = `@Aplication:assistente:empresa:${user?.id || ''}`;
  const chaveFilial = `@Aplication:assistente:filial:${user?.id || ''}`;
  const chaveRegiao = `@Aplication:assistente:regiao:${user?.id || ''}`;
  const [empresaCodigo, setEmpresaCodigo] = useState<string>(() =>
    lerDaSessao(chaveEmpresa, '')
  );
  const [filialId, setFilialId] = useState<string>(() => lerDaSessao(chaveFilial, ''));
  const [regiao, setRegiao] = useState<string>(() => lerDaSessao(chaveRegiao, ''));

  function escolherRegiao(codigo: string) {
    setRegiao(codigo);
    gravarNaSessao(chaveRegiao, codigo);
  }
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [carregandoFiliais, setCarregandoFiliais] = useState(false);
  const [erroFiliais, setErroFiliais] = useState(false);
  const [tentativaFiliais, setTentativaFiliais] = useState(0);
  const empresas = useMemo(() => empresasDasFiliais(filiais), [filiais]);

  function escolherFilial(id: string) {
    setFilialId(id);
    gravarNaSessao(chaveFilial, id);
  }

  // Trocar de empresa volta para "todas as filiais" dela.
  function escolherEmpresa(codigo: string) {
    setEmpresaCodigo(codigo);
    gravarNaSessao(chaveEmpresa, codigo);
    escolherFilial('');
  }

  // Outro login lê a escolha dele (ou nenhuma).
  useEffect(() => {
    setEmpresaCodigo(lerDaSessao(chaveEmpresa, ''));
    setFilialId(lerDaSessao(chaveFilial, ''));
    setRegiao(lerDaSessao(chaveRegiao, ''));
  }, [chaveEmpresa, chaveFilial, chaveRegiao]);

  useEffect(() => {
    if (!user?.id) {
      setFiliais([]);
      return undefined;
    }
    let ativo = true;
    setCarregandoFiliais(true);
    setErroFiliais(false);
    // Sem o prefixo /api-essencial/v1: a rota de empresas fica na raiz do
    // ESF-API (como em ListOrder).
    api
      .get('/empresas')
      .then(({ data }) => {
        if (ativo) setFiliais(filiaisAtivas(data));
      })
      .catch(() => {
        if (ativo) {
          setFiliais([]);
          setErroFiliais(true);
        }
      })
      .finally(() => {
        if (ativo) setCarregandoFiliais(false);
      });
    return () => {
      ativo = false;
    };
  }, [user?.id, tentativaFiliais]);

  // A escolha lembrada que não existe mais (excluída, outro ambiente) volta a
  // ser pedida, em vez de consultar o que não há. Filial lembrada sem empresa
  // (a escolha de antes de haver empresa) leva a empresa dela.
  useEffect(() => {
    if (!filiais.length) return;
    const empresa = empresas.find((e) => e.codigo === empresaCodigo);
    if (empresaCodigo && !empresa) {
      escolherEmpresa('');
      return;
    }
    const filial = filiais.find((f) => f.id === filialId);
    if (filialId && !filial) {
      escolherFilial('');
    } else if (filial && !empresaCodigo && /^\d{4}$/.test(filial.codigo)) {
      setEmpresaCodigo(filial.codigo.slice(0, 2));
      gravarNaSessao(chaveEmpresa, filial.codigo.slice(0, 2));
    } else if (filial && empresa && !empresa.filiais.includes(filial)) {
      escolherFilial('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filiais, empresas, empresaCodigo, filialId]);

  function novaConversa() {
    window.clearTimeout(timerEscrita.current);
    conversa.current += 1;
    setMensagens([]);
    setCarregando(false);
    setEscrevendo(false);
  }

  // Outro login (ou logout) começa do zero: a conversa é de quem perguntou.
  useEffect(novaConversa, [user?.id]);

  // Para a escrita se o provedor sair da tela.
  useEffect(() => () => window.clearTimeout(timerEscrita.current), []);

  // Mostra a resposta aos poucos, seguindo o planoDeEscrita. Os gráficos
  // aparecem quando o texto termina.
  function escreverResposta(textoCompleto: string, graficos: Grafico[] = []) {
    const plano = planoDeEscrita(textoCompleto);
    let passo = 0;

    setMensagens((m) => [...m, { autor: 'ia', texto: '' }]);
    setEscrevendo(true);

    const escreverProxima = () => {
      const { ate, espera } = plano[passo];
      const parcial = textoCompleto.slice(0, ate);
      setMensagens((m) => [...m.slice(0, -1), { autor: 'ia', texto: parcial }]);
      passo += 1;
      if (passo < plano.length) {
        timerEscrita.current = window.setTimeout(escreverProxima, espera);
      } else {
        if (graficos.length) {
          setMensagens((m) => [
            ...m.slice(0, -1),
            { autor: 'ia', texto: textoCompleto, graficos },
          ]);
        }
        setEscrevendo(false);
      }
    };
    timerEscrita.current = window.setTimeout(escreverProxima, 0);
  }

  async function enviar(pergunta: string, formato: FormatoResposta) {
    const texto_limpo = pergunta.trim();
    // Enquanto a resposta anterior é escrita, não envia: o histórico iria com
    // a resposta pela metade.
    if (!texto_limpo || carregando || escrevendo) return;

    const minhaConversa = conversa.current;
    setMensagens((m) => [...m, { autor: 'usuario', texto: texto_limpo }]);
    setCarregando(true);

    try {
      // CONTRATO BACKEND (Murilo): POST /api-essencial/v1/assistente/chat
      // body: { empresa_id, usuario_id, mensagem, historico, formato, graficos }
      // `graficos`: os tipos que esta tela desenha (`true` = barras, linha e pizza).
      // retorno: { resposta: string, graficos?: Grafico[] }
      // O histórico vai só com o texto: para redesenhar, o MCP consulta de novo.
      // `empresa_codigo` é a empresa escolhida no assistente ("01") e
      // `empresa_id`, a filial dela, se houver (o MCP usa a filial quando vêm
      // os dois). Sem escolha vão vazios e a IA pergunta qual: a
      // `user.empresa.id` do login não é a filial dos pedidos (ver `Filial`).
      const corpo = {
        empresa_codigo: empresaCodigo || undefined,
        empresa_id: filialId || undefined,
        regiao: regiao || undefined,
        usuario_id: user?.id,
        mensagem: texto_limpo,
        historico: mensagens.map(({ autor, texto }) => ({ autor, texto })),
        formato,
        graficos: TIPOS_DE_GRAFICO,
      };
      // O MCP só responde a quem está logado: vai junto o mesmo login
      // (Bearer) que o `api` usa com o ESF, já renovado pelo AuthContext, e o
      // MCP o confere com o ESF.
      const response = ASSISTENTE_URL
        ? await axios.post(ASSISTENTE_URL, corpo, {
            headers: { Authorization: api.defaults.headers.common['Authorization'] },
            timeout: 120000,
          })
        : await api.post('/api-essencial/v1/assistente/chat', corpo);

      if (minhaConversa !== conversa.current) return;
      const resposta = response?.data?.resposta;
      const graficos = response?.data?.graficos;
      escreverResposta(
        resposta || 'Não consegui entender. Pode reformular a pergunta?',
        Array.isArray(graficos) ? graficos : []
      );
    } catch (error: any) {
      if (minhaConversa !== conversa.current) return;
      // 401: login expirado. 429: limite de perguntas por minuto do MCP.
      // Outros (MCP fora do ar ou ainda não publicado): aviso de integração.
      const status = error?.response?.status;
      const texto =
        status === 401
          ? 'Sua sessão expirou. Saia e entre de novo para continuar.'
          : status === 429
            ? 'Muitas perguntas em pouco tempo. Aguarde um minuto e tente de novo.'
            : 'O assistente ainda está sendo integrado. Em breve vou responder ' +
              'com os dados de metas, pedidos e desempenho da sua equipe.';
      setMensagens((m) => [...m, { autor: 'ia', texto }]);
    } finally {
      if (minhaConversa === conversa.current) setCarregando(false);
    }
  }

  return (
    <AssistenteContext.Provider
      value={{
        mensagens,
        carregando,
        escrevendo,
        enviar,
        novaConversa,
        filiais,
        carregandoFiliais,
        erroFiliais,
        recarregarFiliais: () => setTentativaFiliais((n) => n + 1),
        empresas,
        empresaCodigo,
        escolherEmpresa,
        filialId,
        escolherFilial,
        regiao,
        escolherRegiao,
      }}
    >
      {children}
    </AssistenteContext.Provider>
  );
}

export function useAssistente() {
  return useContext(AssistenteContext);
}
